import mSpine from '../deps';
import {
  CHARACTER_ANIMATION_ATLAS,
  CHARACTER_ANIMATION_JSON,
  CharacterAnimations,
} from '../shared/constants/characterAnimations';

export interface PlayerListener {
  onReady(): void;
  onAnimationStart(animation: Animation): void;
}

export enum Animation {
  IDLE = 'idle',
  WIN = 'win',
  WIN_2 = 'win2',
  LOSE = 'lose',
  NOD = 'nod',
  WAKEUP = 'wakeup',
  SLEEP = 'sleep',
  FALL_A_SLEEP = 'fall_asleep',
}

const playerWrapper = () => {
  const listeners: PlayerListener[] = [];
  const nodAnimationDelayMillis = 15000;
  const idlesToSleep = 5;
  let isAttached = false;
  let player: any = null;
  let ready = false;
  let error = false;
  let animationState: any = null;
  let nextNodAnimationTime = 0;
  let canPlayNod = true;
  let isSleeping = false;
  let idleCount = 0;
  let currentTrack: any = null;

  const animationListener = {
    /** Invoked when this entry has been set as the current entry. */
    start: (entry: any) => {
      currentTrack = entry;
    },

    /** Invoked when another entry has replaced this entry as the current entry. This entry may continue being applied for
     * mixing. */
    interrupt: (entry: any) => {},

    /** Invoked when this entry is no longer the current entry and will never be applied again. */
    end: (entry: any) => {},

    /** Invoked when this entry will be disposed. This may occur without the entry ever being set as the current entry.
     * References to the entry should not be kept after dispose is called, as it may be destroyed or reused. */
    dispose: (entry: any) => {},

    /** Invoked every time this entry's animation completes a loop. */
    complete: (entry: any) => {
      try {
        if (entry && entry.loop) {
          if (!isSleeping) {
            idleCount++;

            if (idleCount >= idlesToSleep) {
              idleCount = 0;
              isSleeping = true;
              player.setAnimation(CharacterAnimations.FallAsleep, false);
              notifyAnimationStart(Animation.FALL_A_SLEEP);
            }
          }
          return;
        }

        if (currentTrack !== entry) {
          return;
        }

        if (isSleeping) {
          player.setAnimation(CharacterAnimations.Sleep, true);
          notifyAnimationStart(Animation.SLEEP);
          return;
        }

        player.setAnimation(CharacterAnimations.Idle, true);
        notifyAnimationStart(Animation.IDLE);
        idleCount = 0;
        canPlayNod = true;
        isSleeping = false;
        nextNodAnimationTime += 2000;
      } catch (e) {}
    },

    /** Invoked when this entry's animation triggers an event. */
    event: (entry: any) => {},
  };

  const spineConfig = {
    jsonUrl: CHARACTER_ANIMATION_JSON,
    atlasUrl: CHARACTER_ANIMATION_ATLAS,
    animation: CharacterAnimations.Idle,
    alpha: true,
    premultipliedAlpha: false,
    showControls: false,
    showLoading: false,
    viewport: {
      x: -280,
      y: 5,
      width: 560,
      height: 380,
      padLeft: 370,
      padRight: 370,
      padTop: 100,
      padBottom: 100,
    },
    success: (spinePlayer: any) => {
      ready = true;
      if (animationState) {
        animationState.removeListener(animationListener);
      }
      animationState = player.animationState;
      if (animationState) {
        animationState.addListener(animationListener);
      }
      listeners.forEach(l => l.onReady());
    },
    error: (spinePlayer: any, msg: string) => {
      ready = true;
      error = true;
      listeners.forEach(l => l.onReady());
      detach();
    },
  };

  const wakeUp = () => {
    if (isSleeping) {
      canPlayNod = false;
      idleCount = 0;
      isSleeping = false;
      player.setAnimation(CharacterAnimations.WakeUp, false);
      notifyAnimationStart(Animation.WAKEUP);
      return true;
    }

    return false;
  };

  const playAnimation = (animation: Animation): boolean => {
    if (!isAttached) {
      return false;
    }

    switch (animation) {
      case Animation.LOSE:
        player.setAnimation(CharacterAnimations.Lose, false);
        notifyAnimationStart(Animation.LOSE);
        nextNodAnimationTime += 2000;
        break;
      case Animation.WIN:
        player.setAnimation(CharacterAnimations.Win, false);
        notifyAnimationStart(Animation.WIN);
        nextNodAnimationTime += 1000;
        break;
      case Animation.WIN_2:
        player.setAnimation(CharacterAnimations.Win2, false);
        notifyAnimationStart(Animation.WIN_2);
        nextNodAnimationTime += 1000;
        break;
      case Animation.NOD:
        if (wakeUp()) {
          return false;
        }
        if (!canPlayNod) return false;
        const now = Date.now();
        if (now < nextNodAnimationTime) return false;
        nextNodAnimationTime = now + nodAnimationDelayMillis;
        player.setAnimation(CharacterAnimations.Nod, false);
        notifyAnimationStart(Animation.NOD);
        break;
      case Animation.WAKEUP:
        if (!wakeUp()) {
          return false;
        }
        break;
    }

    idleCount = 0;
    isSleeping = false;
    canPlayNod = false;
    return true;
  };

  const notifyAnimationStart = (animation: Animation) => {
    if (!ready || !isAttached) {
      return;
    }
    listeners.forEach(l => l.onAnimationStart(animation));
  };

  const subscribeListener = (listener: PlayerListener) => {
    if (listeners.indexOf(listener) >= 0) {
      return;
    }
    listeners.push(listener);
    if (ready) {
      listener.onReady();
    }
  };

  const unsubscribeListener = (listener: PlayerListener) => {
    listeners.filter(l => l !== listener);
  };

  const attach = (parent: string | HTMLElement) => {
    if (isAttached) {
      detach();
    }

    if (error) {
      return;
    }

    const mParent =
      typeof parent === 'string' ? document.getElementById(parent) : parent;

    if (player === null) {
      player = new mSpine.SpinePlayer(mParent, spineConfig);
    } else {
      player.parent = mParent;
      player.parent.appendChild(player.dom);
      player.play();
    }

    isAttached = true;
  };

  const detach = () => {
    if (player === null || !isAttached) {
      return;
    }
    player.pause();
    player.parent.removeChild(player.dom);
    isAttached = false;
  };

  return {
    attach,
    detach,
    subscribeListener,
    unsubscribeListener,
    playAnimation,
  };
};

const mPlayerWrapper = playerWrapper();
mPlayerWrapper.attach(document.createElement('div'));
export default mPlayerWrapper;
